/**
 * What a nursery spray can be aimed at, worked out from a farm's nursery
 * locations and the lots standing in them (docs/nursery-spraying-plan.md).
 *
 *   nursery_block     a whole nursery block          target_id = block id
 *   nursery_row       one row, or one side of it     target_id = block id, row_number, row_side
 *   nursery_location  one row position, or a place that is not a row
 *
 * The server resolves names, lots and plants again when the spray is saved;
 * this is what the picker shows while somebody is ticking.
 */

import type { NurseryLocation, NurseryLot, RowSide } from '@/types/nursery';
import type { SprayTargetInput } from '@/types/spray';
import { rowLabel } from '@/utils/nurseryRowSlots';

export interface NurseryRowLine {
  key: string;
  blockId: number;
  row: number;
  side: RowSide | null;
  label: string;
  slots: NurseryLocation[];
  plants: number;
  lots: number;
}

export interface NurseryBlockLayout {
  id: number;
  name: string;
  rows: NurseryRowLine[];
  plants: number;
  lots: number;
}

export interface NurseryPlaceLine {
  location: NurseryLocation;
  plants: number;
  lots: number;
}

export interface NurserySprayLayout {
  blocks: NurseryBlockLayout[];
  places: NurseryPlaceLine[];
  plantsByLocation: Map<number, number>;
  lotsByLocation: Map<number, number>;
  locationById: Map<number, NurseryLocation>;
}

export const rowKey = (blockId: number, row: number, side: RowSide | null) =>
  `${blockId}:${row}:${side ?? ''}`;

/** A target's identity, for selection and de-duplication. */
export function targetKey(t: SprayTargetInput): string {
  if (t.target_type === 'nursery_row') return `row:${rowKey(t.target_id, t.row_number ?? 0, t.row_side ?? null)}`;
  return `${t.target_type}:${t.target_id}`;
}

const SIDE_ORDER: Record<string, number> = { '': 0, left: 1, right: 2 };

/** Group a farm's locations into blocks of rows, and places that are not rows. */
export function buildNurseryLayout(locations: NurseryLocation[], lots: NurseryLot[]): NurserySprayLayout {
  const plantsByLocation = new Map<number, number>();
  const lotsByLocation = new Map<number, number>();
  for (const lot of lots) {
    if (lot.location_id == null || lot.status !== 'active' || !(lot.current_count > 0)) continue;
    plantsByLocation.set(lot.location_id, (plantsByLocation.get(lot.location_id) ?? 0) + lot.current_count);
    lotsByLocation.set(lot.location_id, (lotsByLocation.get(lot.location_id) ?? 0) + 1);
  }

  const locationById = new Map<number, NurseryLocation>();
  const blocks = new Map<number, NurseryBlockLayout>();
  const rowLines = new Map<string, NurseryRowLine>();
  const places: NurseryPlaceLine[] = [];

  for (const loc of locations) {
    if (loc.active === false) continue;
    locationById.set(loc.id, loc);
    const plants = plantsByLocation.get(loc.id) ?? 0;
    const lotCount = lotsByLocation.get(loc.id) ?? 0;

    if (loc.location_type === 'nursery_row' && loc.block_id != null && loc.row_number != null) {
      const block = blocks.get(loc.block_id)
        ?? { id: loc.block_id, name: loc.block_name || `Block ${loc.block_id}`, rows: [], plants: 0, lots: 0 };
      blocks.set(loc.block_id, block);
      block.plants += plants;
      block.lots += lotCount;

      const side = loc.row_side ?? null;
      const key = rowKey(loc.block_id, loc.row_number, side);
      let line = rowLines.get(key);
      if (!line) {
        line = { key, blockId: loc.block_id, row: loc.row_number, side, label: rowLabel(loc.row_number, side), slots: [], plants: 0, lots: 0 };
        rowLines.set(key, line);
        block.rows.push(line);
      }
      line.slots.push(loc);
      line.plants += plants;
      line.lots += lotCount;
    } else {
      places.push({ location: loc, plants, lots: lotCount });
    }
  }

  for (const block of blocks.values()) {
    block.rows.sort((a, b) => a.row - b.row || SIDE_ORDER[a.side ?? ''] - SIDE_ORDER[b.side ?? '']);
    for (const line of block.rows) line.slots.sort((a, b) => (a.sequence_number ?? 0) - (b.sequence_number ?? 0));
  }

  return {
    blocks: [...blocks.values()].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })),
    places: places.sort((a, b) => a.location.name.localeCompare(b.location.name, undefined, { numeric: true })),
    plantsByLocation,
    lotsByLocation,
    locationById,
  };
}

/**
 * "1-12, 15" → [1..12, 15]. Rows are counted one by one: unlike row
 * positions, a range of rows is every row in it. Anything that is not a
 * positive whole number is dropped.
 */
export function parseRowList(input: string): number[] {
  const out = new Set<number>();
  for (const token of input.split(/[,\s]+/)) {
    const range = /^(\d+)-(\d+)$/.exec(token);
    if (range) {
      const from = Number(range[1]);
      const to = Math.min(Number(range[2]), from + 2000);
      for (let n = from; n <= to; n++) if (n > 0) out.add(n);
    } else if (/^\d+$/.test(token) && Number(token) > 0) {
      out.add(Number(token));
    }
  }
  return [...out].sort((a, b) => a - b);
}

/**
 * The locations a target covers, so a lot standing in two ticked targets (a
 * whole block and one of its rows) is counted once, as the server records it.
 */
export function locationsForTarget(t: SprayTargetInput, layout: NurserySprayLayout): number[] {
  if (t.target_type === 'nursery_location') return [t.target_id];
  const block = layout.blocks.find(b => b.id === t.target_id);
  if (!block) return [];
  if (t.target_type === 'nursery_block') {
    const inRows = block.rows.flatMap(r => r.slots.map(s => s.id));
    const placesInBlock = layout.places.filter(p => p.location.block_id === block.id).map(p => p.location.id);
    return [...inRows, ...placesInBlock];
  }
  if (t.target_type === 'nursery_row') {
    // A row with no side named is the whole row: both sides.
    return block.rows
      .filter(r => r.row === t.row_number && (t.row_side == null || r.side === t.row_side))
      .flatMap(r => r.slots.map(s => s.id));
  }
  return [];
}

export interface NurseryTargetSummary {
  plants: number;
  lots: number;
  /** Ticked targets with nothing standing in them. */
  empty: number;
}

export function summariseTargets(targets: SprayTargetInput[], layout: NurserySprayLayout): NurseryTargetSummary {
  const seen = new Set<number>();
  let empty = 0;
  for (const t of targets) {
    const ids = locationsForTarget(t, layout);
    if (!ids.some(id => (layout.plantsByLocation.get(id) ?? 0) > 0)) empty++;
    ids.forEach(id => seen.add(id));
  }
  let plants = 0;
  let lots = 0;
  for (const id of seen) {
    plants += layout.plantsByLocation.get(id) ?? 0;
    lots += layout.lotsByLocation.get(id) ?? 0;
  }
  return { plants, lots, empty };
}

/**
 * Build the targets a "Spray these rows" link carries: `rows=5:left,6:,7:right`
 * (an empty side is the whole row) and `locations=12,13`.
 */
export function targetsFromParams(params: URLSearchParams): SprayTargetInput[] {
  const out: SprayTargetInput[] = [];
  const block = Number(params.get('block'));
  if (Number.isInteger(block) && block > 0) {
    if (params.get('whole_block') === '1') {
      out.push({ target_type: 'nursery_block', target_id: block });
    }
    for (const token of (params.get('rows') ?? '').split(',').filter(Boolean)) {
      const [row, side] = token.split(':');
      const n = Number(row);
      if (!Number.isInteger(n) || n < 0) continue;
      out.push({
        target_type: 'nursery_row',
        target_id: block,
        row_number: n,
        row_side: side === 'left' || side === 'right' ? side : null,
      });
    }
  }
  for (const id of (params.get('locations') ?? '').split(',').filter(Boolean)) {
    const n = Number(id);
    if (Number.isInteger(n) && n > 0) out.push({ target_type: 'nursery_location', target_id: n });
  }
  return out;
}

/** The reverse of targetsFromParams, for building a link. */
export function sprayLinkFor(opts: {
  farmId: number | null | undefined;
  blockId?: number | null;
  rows?: { row: number; side: RowSide | null }[];
  locationIds?: number[];
}): string {
  const p = new URLSearchParams({ mode: 'nursery' });
  if (opts.farmId) p.set('farm', String(opts.farmId));
  if (opts.blockId && opts.rows?.length) {
    p.set('block', String(opts.blockId));
    p.set('rows', opts.rows.map(r => `${r.row}:${r.side ?? ''}`).join(','));
  }
  if (opts.locationIds?.length) p.set('locations', opts.locationIds.join(','));
  return `/spray/new?${p.toString()}`;
}
